import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

/**
 * Reusable full-screen image lightbox built on Shadcn Dialog.
 * - Pass a single string to `images` for a single-image lightbox.
 * - Pass an array for a navigable gallery (prev/next + counter).
 *
 * Props:
 *   open, onOpenChange  -> dialog visibility (controlled)
 *   images              -> string | string[]
 *   index, onIndexChange (only used when images is an array)
 *   title, description  -> a11y (sr-only)
 *   imageAlt            -> base alt text (index appended for galleries)
 */
export const ImageLightbox = ({
  open,
  onOpenChange,
  images,
  index = 0,
  onIndexChange,
  title,
  description,
  imageAlt = 'Imagem',
  contentSrcSet,
}) => {
  const list = Array.isArray(images) ? images : [images];
  const isGallery = list.length > 1;
  const currentSrc = list[index] || list[0];
  const close = () => onOpenChange(false);
  const next = () => onIndexChange?.((index + 1) % list.length);
  const prev = () => onIndexChange?.((index - 1 + list.length) % list.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-full md:max-w-4xl w-full p-0 bg-white md:bg-black/95 border-none overflow-hidden [&>button]:hidden max-h-screen md:max-h-[90vh]"
        data-testid="image-lightbox-content"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        <div className={`relative w-full flex flex-col h-screen ${isGallery ? 'md:h-[80vh]' : 'md:h-[70vh]'}`}>
          {/* Mobile top bar */}
          <div className="md:hidden absolute top-0 left-0 right-0 z-[60] bg-gradient-to-b from-black/60 to-transparent p-4 flex justify-between items-center">
            <button
              onClick={close}
              className="bg-red-600 hover:bg-red-700 rounded-full p-3 shadow-lg transition-colors"
              aria-label="Fechar"
              data-testid="lightbox-close-mobile-top"
            >
              <X className="text-white" size={24} />
            </button>
            {isGallery && (
              <div className="bg-black/50 px-4 py-2 rounded-full">
                <p className="text-white text-sm font-medium" data-testid="lightbox-counter-mobile">
                  {index + 1} / {list.length}
                </p>
              </div>
            )}
          </div>

          {/* Desktop close button */}
          <button
            onClick={close}
            className="hidden md:block absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Fechar"
            data-testid="lightbox-close-desktop"
          >
            <X className="text-white" size={24} />
          </button>

          {/* Image container */}
          <div className="flex-1 relative flex items-center justify-center overflow-hidden">
            {isGallery ? (
              <AnimatePresence mode="sync">
                <motion.img
                  key={index}
                  src={currentSrc}
                  alt={`${imageAlt} ${index + 1}`}
                  className="w-full h-full object-contain md:object-contain md:h-[80vh] absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  data-testid="lightbox-image"
                />
              </AnimatePresence>
            ) : (
              <motion.picture
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="w-full h-full flex items-center justify-center"
              >
                {contentSrcSet && (
                  <source srcSet={contentSrcSet} type="image/webp" />
                )}
                <img
                  src={currentSrc}
                  alt={imageAlt}
                  className="w-full h-full object-contain md:object-contain md:h-auto"
                  data-testid="lightbox-image"
                />
              </motion.picture>
            )}
          </div>

          {/* Mobile bottom bar */}
          {isGallery ? (
            <div className="md:hidden absolute bottom-0 left-0 right-0 z-[60] bg-gradient-to-t from-black/60 to-transparent p-4 flex justify-between items-center gap-4">
              <button
                onClick={prev}
                className="bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-colors"
                aria-label="Imagem anterior"
                data-testid="lightbox-prev-mobile"
              >
                <ChevronLeft className="text-gray-900" size={28} />
              </button>
              <button
                onClick={close}
                className="flex-1 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-full shadow-xl transition-colors flex items-center justify-center gap-2"
                data-testid="lightbox-close-mobile-bottom"
              >
                <X className="text-white" size={20} />
                <span className="text-white font-semibold">Fechar</span>
              </button>
              <button
                onClick={next}
                className="bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-colors"
                aria-label="Próxima imagem"
                data-testid="lightbox-next-mobile"
              >
                <ChevronRight className="text-gray-900" size={28} />
              </button>
            </div>
          ) : (
            <div className="md:hidden absolute bottom-0 left-0 right-0 z-[60] bg-gradient-to-t from-black/60 to-transparent p-4">
              <button
                onClick={close}
                className="w-full bg-red-600 hover:bg-red-700 px-6 py-3 rounded-full shadow-xl transition-colors flex items-center justify-center gap-2"
                data-testid="lightbox-close-mobile-only"
              >
                <X className="text-white" size={20} />
                <span className="text-white font-semibold">Fechar</span>
              </button>
            </div>
          )}

          {/* Desktop nav buttons (gallery only) */}
          {isGallery && (
            <>
              <button
                onClick={prev}
                className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-gray-100 rounded-full p-3 transition-all shadow-xl items-center justify-center hover:scale-105"
                aria-label="Imagem anterior"
                data-testid="lightbox-prev-desktop"
              >
                <ChevronLeft className="text-gray-900" size={24} strokeWidth={2.5} />
              </button>
              <button
                onClick={next}
                className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-gray-100 rounded-full p-3 transition-all shadow-xl items-center justify-center hover:scale-105"
                aria-label="Próxima imagem"
                data-testid="lightbox-next-desktop"
              >
                <ChevronRight className="text-gray-900" size={24} strokeWidth={2.5} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <p className="text-white font-medium" data-testid="lightbox-counter-desktop">
                  {index + 1} / {list.length}
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
